import { call, put, select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { APIResult, callAPI } from '../../callApi';
import { createAsyncActionResource } from '../util';
import {
  ApiActionType,
  EndPointBuilder,
  HTTPMethod,
  InsertPayload,
  ItemBodyMerger,
  ItemBodyParser,
  ItemSummaryBodyMerger,
  KeyComparer,
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
  OnSuccessHandler,
} from './types';
import { buildRequestBody, createErrorHandler, handleBodyLoaded } from './util';

type SuccessPayload<TItemBody> = {
  frameSeqNumber: number;
  response: APIResult;
  onSuccess?: OnSuccessHandler<TItemBody>;
};

export function createInsertAction<
  TItemKey,
  TItemSummary extends TItemKey,
  TItemBody extends TItemKey,
  TItemBodyAPI,
>(
  displayName: string,
  getEndpoint: EndPointBuilder<TItemKey, TItemSummary, TItemBody>,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  parseItemBody: ItemBodyParser<TItemBody, TItemBodyAPI>,
  mergeItemBody: ItemBodyMerger<TItemBody>,
  mergeSummaryBody: ItemSummaryBodyMerger<TItemSummary, TItemBody>,
) {
  const insertAsync = createAsyncActionResource<
    InsertPayload<TItemBody, TItemKey>,
    SuccessPayload<TItemBody>
  >(displayName, 'insert');

  function* insertRequestSaga(
    params: ActionType<typeof insertAsync.request>,
  ): Generator {
    try {
      const bodyGET = yield* buildRequestBody(
        selector,
        params.payload.bodyFragment,
      );
      const { frameSeqNumber } = (yield select(selector)) as MainResourceState<
        TItemKey,
        TItemSummary,
        TItemBody
      >;
      const bodyPOST = {
        ...params.payload.data,
        ...params.payload.body,
      };

      const body = { bodyGET, bodyPOST };
      const response = yield* call(
        callAPI,
        getEndpoint(),
        'POST' as HTTPMethod,
        {
          body,
          lockNum: 1,
        },
      );

      yield put(
        insertAsync.success({
          frameSeqNumber,
          response,
          onSuccess: params.payload.onSuccess,
        }),
      );
    } catch (error) {
      // TODO add support for onFailure
      yield put(insertAsync.failure(error));
    }
  }

  const insertRequestHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof insertAsync.request
  > = state => {
    return {
      ...state,
      pendingUpdates: state.pendingUpdates + 1,
      lastLoadParams: undefined,
      error: undefined,
      patchData: undefined,
    };
  };

  const insertSuccessHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof insertAsync.success
  > = (state, action) => {
    return handleBodyLoaded(
      state,
      action.payload.response,
      action.payload.frameSeqNumber,
      true,
      false,
      true,
      sameKey,
      mergeItemBody,
      mergeSummaryBody,
      parseItemBody,
      ApiActionType.insert,
    );
  };

  const insertFailureHandler = createErrorHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof insertAsync.failure
  >(true);

  function* insertSuccessSaga(action: ActionType<typeof insertAsync.success>) {
    const { onSuccess } = action.payload;

    if (onSuccess) {
      const { currentBody } = (yield select(selector)) as MainResourceState<
        TItemKey,
        TItemSummary,
        TItemBody
      >;
      onSuccess(currentBody);
    }
  }

  function* insertFailureSaga() {
    // TODO
  }

  return {
    insertAsync,
    insertRequestSaga,
    insertRequestHandler,
    insertSuccessSaga,
    insertSuccessHandler,
    insertFailureSaga,
    insertFailureHandler,
  };
}
